import { useState } from "react";
import { mutate } from "swr";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import useToast from "~/components/ui/toast";
import { ACCENT_PALETTE, resolveAccentPaletteColor } from "~/lib/accentPalette";
import { ColorSwatchGrid } from "~/pages/manifold-nextgen/components/ColorSwatchGrid";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import {
  PartialProject,
  mutateProjectList,
  projectPath,
  projectRouteId,
  updateProject,
} from "~/services/projects";

interface ProjectEditDialogProps {
  project: PartialProject;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** The only way to rename or recolour a project. */
export const ProjectEditDialog = ({ project, open, onOpenChange }: ProjectEditDialogProps) => {
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // Captured once per mount (the dialog remounts each time it opens), so it stays
  // stable as the Reset-equivalent baseline for the isDirty check below.
  const [committedColor] = useState(
    () => resolveAccentPaletteColor(project.accent_color ?? undefined).accent
  );
  const [color, setColor] = useState(committedColor);
  const [name, setName] = useState(project.name);

  const trimmedName = name.trim();
  const renamed = trimmedName !== project.name;
  const recolored = color !== committedColor;
  const isDirty = recolored || renamed;
  const previewColor = resolveAccentPaletteColor(color);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Mutate by the project's route id (short_id, falling back to the uuid), not
      // `project.id` directly — that's the cache key ProjectLayout's own fetch uses,
      // so this is what actually refreshes the overview page still showing this project.
      const routeId = projectRouteId(project);
      // Only what changed: an unset accent_color resolves to the palette default
      // for display, and echoing that back would persist it as a real choice.
      await updateProject(routeId, {
        ...(recolored ? { accent_color: color } : {}),
        ...(renamed ? { name: trimmedName } : {}),
      });
      await Promise.all([mutate(projectPath(routeId)), mutateProjectList()]);
      showSuccess("Project saved");
      onOpenChange(false);
    } catch {
      showError("Failed to save project");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit name and appearance"
      contentClassName="sm:max-w-2xl"
      onPrimary={handleSave}
      primaryLabel="Save"
      primaryPendingLabel="Saving…"
      primaryDisabled={!isDirty || !trimmedName}
      isPending={isSaving}
    >
      <div className="grid grid-cols-[280px_1fr] gap-8 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <div className="relative h-[246px] w-[200px] overflow-hidden rounded-xl rounded-l-lg border border-border bg-card">
            <div
              className="absolute inset-y-0 left-0 w-8 overflow-hidden rounded-l-lg"
              style={{ backgroundColor: previewColor.bg }}
            />
            <div className="ml-8 flex h-full flex-col gap-1 px-3 py-4">
              <p className="truncate text-sm font-semibold text-card-foreground">
                {trimmedName || project.name}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <Label htmlFor="project-name" className="mb-2 text-sm font-medium text-foreground">
              Name
            </Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <ColorSwatchGrid colors={ACCENT_PALETTE} color={color} onChange={setColor} />
          </div>
        </div>
      </div>
    </FormDialog>
  );
};
